import React, { useState, useEffect, useMemo } from 'react';
import { 
  X, 
  TrendingUp, 
  AlertTriangle, 
  Calculator, 
  DollarSign, 
  Calendar, 
  Target, 
  ExternalLink, 
  Zap, 
  Award,
  Building2,
  Percent,
  Euro,
  Clock,
  FileText,
  Infinity
} from 'lucide-react';

const FinancingModal = ({ 
  isOpen, 
  onClose, 
  financing, 
  properties = [],
  onSave,
  errors = {},
  loading = false,
  existingFinancings = []
}) => {
  // Initialize form data with safe defaults
  const [formData, setFormData] = useState({
    lender_name: '',
    loan_amount: '',
    interest_rate_apy: '',
    amortization_rate_apy: '',
    repayment_type: 'annuity',
    start_date: '',
    maturity_date: '',
    fixed_rate_end_date: '',
    grace_months: 0,
    balloon_amount: '',
    origination_fee: '',
    payment_frequency: 'monthly',
    index_ref: '',
    spread_bps: '',
    notes: '',
    property_allocations: []
  });

  const [activeInsight, setActiveInsight] = useState('benchmark');

  // Body scroll lock effect - CORRECTED VERSION
  useEffect(() => {
    if (isOpen) {
      // Lock body scrolling
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = "0px"; // Prevent layout shift
    }
    
    // Cleanup function
    return () => {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    };
  }, [isOpen]);

  const [calculationMode, setCalculationMode] = useState('tilgung'); // 'tilgung' or 'rate'
  const [bankSuggestions, setBankSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Extract unique bank names from existing financings for autocomplete
  const uniqueBankNames = useMemo(() => {
    if (!existingFinancings || existingFinancings.length === 0) return [];
    
    const banks = existingFinancings
      .map(f => f.lender_name)
      .filter(name => name && name.trim())
      .filter((name, index, arr) => arr.indexOf(name) === index)
      .sort();
    
    return banks;
  }, [existingFinancings]);

  // Handle bank name input with autocomplete
  const handleBankNameChange = (value) => {
    setFormData(prev => ({ ...prev, lender_name: value }));
    
    if (value.length > 0) {
      const filtered = uniqueBankNames.filter(bank =>
        bank.toLowerCase().includes(value.toLowerCase())
      );
      setBankSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setBankSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Select bank from suggestions
  const selectBankSuggestion = (bankName) => {
    setFormData(prev => ({ ...prev, lender_name: bankName }));
    setShowSuggestions(false);
    setBankSuggestions([]);
  };

  // Mock market data - in production, this would come from real APIs
  const marketData = {
    averageRate: 2.85,
    bestRate: 2.45,
    trends: {
      direction: 'rising',
      change: 0.15,
      forecast: 'Zinsen steigen voraussichtlich um weitere 0.2% in den nächsten 6 Monaten'
    }
  };

  // Calculate loan prediction
  const calculateLoanPrediction = useMemo(() => {
    const loanAmount = parseFloat(formData.loan_amount) || 0;
    const interestRate = parseFloat(formData.interest_rate_apy) || 0;
    const amortizationRate = parseFloat(formData.amortization_rate_apy) || 0;
    const startDate = formData.start_date ? new Date(formData.start_date) : null;

    if (!loanAmount || !interestRate || !startDate || formData.repayment_type !== 'annuity') {
      return null;
    }

    // If no amortization (interest-only), return infinity
    if (!amortizationRate || amortizationRate <= 0) {
      return { isInfinite: true };
    }

    try {
      // Calculate loan duration using the formula for annuity loans
      const monthlyInterestRate = interestRate / 100 / 12;
      const monthlyAmortizationRate = amortizationRate / 100 / 12;
      const monthlyPaymentRate = monthlyInterestRate + monthlyAmortizationRate;
      const monthlyPayment = loanAmount * monthlyPaymentRate;

      // Calculate number of months to pay off the loan
      const numerator = Math.log(1 - (loanAmount * monthlyInterestRate) / monthlyPayment);
      const denominator = Math.log(1 + monthlyInterestRate);
      const monthsToPayOff = -numerator / denominator;

      if (!isFinite(monthsToPayOff) || monthsToPayOff <= 0) {
        return { isInfinite: true };
      }

      // Calculate end date
      const endDate = new Date(startDate);
      endDate.setMonth(endDate.getMonth() + Math.ceil(monthsToPayOff));

      const years = Math.floor(monthsToPayOff / 12);
      const months = Math.ceil(monthsToPayOff % 12);

      return {
        isInfinite: false,
        endDate,
        totalMonths: Math.ceil(monthsToPayOff),
        years,
        months,
        monthlyPayment
      };
    } catch (error) {
      console.error('Error calculating loan prediction:', error);
      return null;
    }
  }, [formData.loan_amount, formData.interest_rate_apy, formData.amortization_rate_apy, formData.start_date, formData.repayment_type]);

  // Smart calculator: bidirectional calculation between rate and amortization
  const handleSmartCalculation = (field, value) => {
    const loanAmount = parseFloat(formData.loan_amount) || 0;
    const interestRate = parseFloat(formData.interest_rate_apy) || 0;

    // Allow calculation with partial data - improved logic    if (field === "monthly_rate" && value && loanAmount && interestRate) {

    if (field === 'monthly_rate' && value) {
      // Calculate amortization rate from monthly rate
      const monthlyRate = parseFloat(value);
      const monthlyInterest = (loanAmount * interestRate / 100) / 12;
      const monthlyAmortization = monthlyRate - monthlyInterest;
      const annualAmortization = (monthlyAmortization * 12 / loanAmount) * 100;
      
      if (annualAmortization > 0) {
        setFormData(prev => ({ 
          ...prev, 
          amortization_rate_apy: annualAmortization.toFixed(2),
          monthly_rate: value
        }));
        setCalculationMode('rate');
      }
    } else if (field === 'amortization_rate_apy' && value) {
      // Calculate monthly rate from amortization rate
      const amortizationRate = parseFloat(value);
      const monthlyPayment = (loanAmount * (interestRate + amortizationRate) / 100) / 12;
      
      setFormData(prev => ({ 
        ...prev, 
        amortization_rate_apy: value,
        monthly_rate: monthlyPayment.toFixed(2)
      }));
      setCalculationMode('tilgung');
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  // Calculate insights based on current form data - SAFE VERSION
  const insights = useMemo(() => {
    // Safe access to form data with fallbacks
    const currentRate = parseFloat(formData?.interest_rate_apy || 0);
    const loanAmount = parseFloat(formData?.loan_amount || 0);
    const amortizationRate = parseFloat(formData?.amortization_rate_apy || 0);
    
    // Only calculate if we have valid data
    if (!currentRate || !loanAmount) {
      return {
        rateComparison: { vsMarket: 0, vsBest: 0, potentialSavings: 0 },
        monthlyPayment: 0,
        riskLevel: 'low',
        recommendations: []
      };
    }

    // Rate comparison
    const rateComparison = {
      vsMarket: currentRate - marketData.averageRate,
      vsBest: currentRate - marketData.bestRate,
      potentialSavings: loanAmount * (currentRate - marketData.bestRate) / 100
    };

    // Payment calculation
    let monthlyPayment = 0;
    if (formData.repayment_type === 'annuity' && amortizationRate) {
      // Annuity loan: principal + interest
      monthlyPayment = (loanAmount * (currentRate + amortizationRate) / 100) / 12;
    } else if (formData.repayment_type === 'linear' && amortizationRate) {
      // Linear loan: fixed principal + decreasing interest (approximate)
      monthlyPayment = (loanAmount * amortizationRate / 100) / 12 + (loanAmount * currentRate / 100) / 12;
    } else if (formData.repayment_type === 'variable' && amortizationRate) {
      // Variable loan: similar to annuity but with variable rate
      monthlyPayment = (loanAmount * (currentRate + amortizationRate) / 100) / 12;
    } else {
      // Interest-only loan
      monthlyPayment = (loanAmount * currentRate / 100) / 12;
    }

    // Risk assessment
    const riskFactors = [];
    if (currentRate > marketData.averageRate + 0.5) riskFactors.push('Hoher Zinssatz');
    if (!formData?.fixed_rate_end_date) riskFactors.push('Variable Zinsen');
    if (loanAmount > 500000) riskFactors.push('Hohes Volumen');

    const riskLevel = riskFactors.length > 2 ? 'high' : riskFactors.length > 0 ? 'medium' : 'low';

    // Recommendations
    const recommendations = [];
    if (rateComparison.vsMarket > 0.3) {
      recommendations.push('Prüfen Sie alternative Anbieter für bessere Konditionen');
    }
    if (!formData?.fixed_rate_end_date) {
      recommendations.push('Erwägen Sie eine Zinsbindung für Planungssicherheit');
    }
    if (rateComparison.potentialSavings > 5000) {
      recommendations.push('Umschuldung könnte sich lohnen');
    }

    return {
      rateComparison,
      monthlyPayment,
      riskLevel,
      recommendations
    };
  }, [formData, marketData]);

  // Initialize form data when modal opens
  useEffect(() => {
    if (isOpen) {
      if (financing) {
        // Edit mode: populate with existing data
        setFormData({
          lender_name: financing.lender_name || '',
          loan_amount: financing.loan_amount || '',
          interest_rate_apy: financing.interest_rate_apy || '',
          amortization_rate_apy: financing.amortization_rate_apy || '',
          repayment_type: financing.repayment_type || 'annuity',
          start_date: financing.start_date || '',
          maturity_date: financing.maturity_date || '',
          fixed_rate_end_date: financing.fixed_rate_end_date || '',
          grace_months: financing.grace_months || 0,
          balloon_amount: financing.balloon_amount || '',
          origination_fee: financing.origination_fee || '',
          payment_frequency: financing.payment_frequency || 'monthly',
          index_ref: financing.index_ref || '',
          spread_bps: financing.spread_bps || '',
          notes: financing.notes || '',
          property_allocations: financing.financing_properties?.map(fp => ({
            property_id: fp.property?.id,
            allocation_ratio: fp.allocation_ratio
          })) || []
        });
      } else {
        // New mode: reset to defaults
        setFormData({
          lender_name: '',
          loan_amount: '',
          interest_rate_apy: '',
          amortization_rate_apy: '',
          repayment_type: 'annuity',
          start_date: '',
          maturity_date: '',
          fixed_rate_end_date: '',
          grace_months: 0,
          balloon_amount: '',
          origination_fee: '',
          payment_frequency: 'monthly',
          index_ref: '',
          spread_bps: '',
          notes: '',
          property_allocations: []
        });
      }
      setActiveInsight('benchmark');
      setCalculationMode('tilgung');
    }
  }, [isOpen, financing]);

  const handleInputChange = (field, value) => {
    if (field === 'lender_name') {
      handleBankNameChange(value);
    } else if (field === 'monthly_rate' || field === 'amortization_rate_apy') {
      handleSmartCalculation(field, value);
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatPercentage = (rate) => {
    return `${parseFloat(rate || 0).toFixed(2)}%`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-DE');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        <div className="flex flex-1 min-h-0">
          {/* Left Side - Form */}
          <div className="flex-1 p-6 overflow-y-auto min-h-0">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                {financing ? 'Finanzierung bearbeiten' : 'Neue Finanzierung'}
              </h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Bankname *
                  </label>
                  <input
                    type="text"
                    value={formData.lender_name}
                    onChange={(e) => handleInputChange('lender_name', e.target.value)}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-gray-400"
                    placeholder="z.B. Sparkasse, Deutsche Bank..."
                    required
                  />
                  {showSuggestions && bankSuggestions.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                      {bankSuggestions.map((bank, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => selectBankSuggestion(bank)}
                          className="w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-white"
                        >
                          {bank}
                        </button>
                      ))}
                    </div>
                  )}
                  {errors.lender_name && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.lender_name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Darlehenssumme (€) *
                  </label>
                  <input
                    type="number"
                    value={formData.loan_amount}
                    onChange={(e) => handleInputChange('loan_amount', e.target.value)}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 dark:text-white"
                    placeholder="500000"
                    required
                  />
                  {errors.loan_amount && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.loan_amount}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Zinssatz p.a. (%) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.interest_rate_apy}
                    onChange={(e) => handleInputChange('interest_rate_apy', e.target.value)}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 dark:text-white"
                    placeholder="3.50"
                    required
                  />
                  {errors.interest_rate_apy && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.interest_rate_apy}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Darlehenstyp
                  </label>
                  <select
                    value={formData.repayment_type}
                    onChange={(e) => handleInputChange('repayment_type', e.target.value)}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 dark:text-white"
                  >
                    <option value="annuity">Annuitätendarlehen</option>
                    <option value="interest_only">Endfälliges Darlehen</option>
                    <option value="linear">Lineares Darlehen</option>
                    <option value="balloon">Ballondarlehen</option>
                  </select>
                </div>
              </div>

              {/* Smart Calculator Section */}
              {(formData.repayment_type === 'annuity' || formData.repayment_type === 'variable' || formData.repayment_type === 'linear') && (
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <Calculator className="w-5 h-5 text-blue-600" />
                    Smart Calculator
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Tilgungssatz p.a. (%)
                        {calculationMode === 'rate' && (
                          <span className="ml-2 text-xs text-green-600 dark:text-green-400">✓ Berechnet</span>
                        )}
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.amortization_rate_apy}
                        onChange={(e) => handleSmartCalculation('amortization_rate_apy', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 dark:text-white ${
                          calculationMode === 'rate' 
                            ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-600' 
                            : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600'
                        }`}
                        placeholder="2.00"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Monatliche Rate (€)
                        {calculationMode === 'tilgung' && (
                          <span className="ml-2 text-xs text-green-600 dark:text-green-400">✓ Berechnet</span>
                        )}
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.monthly_rate || ''}
                        onChange={(e) => { setFormData(prev => ({ ...prev, monthly_rate: e.target.value })); handleSmartCalculation('monthly_rate', e.target.value); }}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 dark:text-white ${
                          calculationMode === 'tilgung' 
                            ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-600' 
                            : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600'
                        }`}
                        placeholder="Wird berechnet..."
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                  {/* Breakdown Display */}
                  {formData.loan_amount && formData.interest_rate_apy && formData.monthly_rate && (
                    <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">Aufschlüsselung der monatlichen Rate:</h4>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-blue-700 dark:text-blue-300">Zinsanteil:</span>
                          <span className="ml-2 font-semibold text-blue-900 dark:text-blue-100">
                            {(() => {
                              const loanAmount = parseFloat(formData.loan_amount) || 0;
                              const interestRate = parseFloat(formData.interest_rate_apy) || 0;
                              const monthlyInterest = (loanAmount * interestRate / 100) / 12;
                              return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(monthlyInterest);
                            })()}
                          </span>
                        </div>
                        <div>
                          <span className="text-blue-700 dark:text-blue-300">Tilgungsanteil:</span>
                          <span className="ml-2 font-semibold text-blue-900 dark:text-blue-100">
                            {(() => {
                              const monthlyRate = parseFloat(formData.monthly_rate) || 0;
                              const loanAmount = parseFloat(formData.loan_amount) || 0;
                              const interestRate = parseFloat(formData.interest_rate_apy) || 0;
                              const monthlyInterest = (loanAmount * interestRate / 100) / 12;
                              const monthlyAmortization = monthlyRate - monthlyInterest;
                              return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(monthlyAmortization);
                            })()}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                    💡 Geben Sie entweder den Tilgungssatz oder die gewünschte monatliche Rate ein - das andere wird automatisch berechnet.
                  </p>
                </div>
              )}

              {/* Loan Prediction */}
              {calculateLoanPrediction && (
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <Target className="w-5 h-5 text-green-600" />
                    Prognostiziertes Kreditende
                  </h3>
                  {calculateLoanPrediction.isInfinite ? (
                    <div className="flex items-center gap-2 text-orange-600 dark:text-orange-400">
                      <Infinity className="w-5 h-5" />
                      <span className="font-semibold">∞ (Keine Tilgung)</span>
                      <span className="text-sm">- Endfälliges Darlehen</span>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Voraussichtliches Ende:</p>
                        <p className="text-lg font-semibold text-green-600 dark:text-green-400">
                          {formatDate(calculateLoanPrediction.endDate)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Gesamtlaufzeit:</p>
                        <p className="text-lg font-semibold text-gray-900 dark:text-white">
                          {calculateLoanPrediction.years} Jahre {calculateLoanPrediction.months} Monate
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Monatliche Rate:</p>
                        <p className="text-lg font-semibold text-gray-900 dark:text-white">
                          {formatCurrency(calculateLoanPrediction.monthlyPayment)}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Dates */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Startdatum *
                  </label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => handleInputChange('start_date', e.target.value)}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 dark:text-white"
                    required
                  />
                  {errors.start_date && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.start_date}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Zinsbindungsende
                  </label>
                  <input
                    type="date"
                    value={formData.fixed_rate_end_date}
                    onChange={(e) => handleInputChange('fixed_rate_end_date', e.target.value)}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 dark:text-white"
                  />
                  {!formData.fixed_rate_end_date && (
                    <p className="mt-1 text-xs text-orange-600 dark:text-orange-400">(Variabel)</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Laufzeitende
                  </label>
                  <input
                    type="date"
                    value={formData.maturity_date}
                    onChange={(e) => handleInputChange('maturity_date', e.target.value)}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              {/* Additional Terms */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Tilgungsfreie Monate
                  </label>
                  <input
                    type="number"
                    value={formData.grace_months}
                    onChange={(e) => handleInputChange('grace_months', e.target.value)}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 dark:text-white"
                    placeholder="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Bearbeitungsgebühr (€)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.origination_fee}
                    onChange={(e) => handleInputChange('origination_fee', e.target.value)}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 dark:text-white"
                    placeholder="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Balloon-Betrag (€)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.balloon_amount}
                    onChange={(e) => handleInputChange('balloon_amount', e.target.value)}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 dark:text-white"
                    placeholder="0"
                  />
                </div>
              </div>

              {/* Variable Rate Terms */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Referenzindex
                  </label>
                  <input
                    type="text"
                    value={formData.index_ref}
                    onChange={(e) => handleInputChange('index_ref', e.target.value)}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 dark:text-white"
                    placeholder="z.B. EURIBOR 3M"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Aufschlag (Basispunkte)
                  </label>
                  <input
                    type="number"
                    value={formData.spread_bps}
                    onChange={(e) => handleInputChange('spread_bps', e.target.value)}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 dark:text-white"
                    placeholder="150"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Zahlungsrhythmus
                  </label>
                  <select
                    value={formData.repayment_type}
                    onChange={(e) => handleInputChange('repayment_type', e.target.value)}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 dark:text-white"
                  >
                    <option value="annuity">Annuitätendarlehen</option>
                    <option value="interest_only">Endfälliges Darlehen</option>
                    <option value="linear">Lineares Darlehen</option>
                    <option value="variable">Variable Darlehen</option>
                  </select>
                </div>
              </div>

              {/* Property Allocation */}
              {/* Object Allocation - Dropdown Interface */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Objektzuordnung
                </label>
                
                {/* Add New Allocation */}
                <div className="flex items-center gap-3 mb-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <select
                    value=""
                    onChange={(e) => {
                      if (e.target.value) {
                        const propertyId = e.target.value;
                        const property = properties.find(p => p.id === propertyId);
                        if (property && !formData.property_allocations.some(pa => pa.property_id === propertyId)) {
                          setFormData(prev => ({
                            ...prev,
                            property_allocations: [...prev.property_allocations, {
                              property_id: propertyId,
                              allocation_ratio: 100
                            }]
                          }));
                        }
                      }
                    }}
                    className="flex-1 px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 dark:text-white"
                  >
                    <option value="">Objekt auswählen...</option>
                    {properties.filter(property => 
                      !formData.property_allocations.some(pa => pa.property_id === property.id)
                    ).map(property => (
                      <option key={property.id} value={property.id}>
                        {property.property_name} - {property.city}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Selected Allocations Table */}
                {formData.property_allocations.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Zugeordnete Objekte:</h4>
                    {formData.property_allocations.map((allocation, index) => {
                      const property = properties.find(p => p.id === allocation.property_id);
                      return (
                        <div key={allocation.property_id} className="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                          <span className="flex-1 text-sm text-gray-900 dark:text-white">
                            {property ? `${property.property_name} - ${property.city}` : "Unbekanntes Objekt"}
                          </span>
                          <div className="flex items-center gap-2">
                            <input
                              type="text" inputMode="numeric"
                              
                              
                              value={allocation.allocation_ratio}
                              onChange={(e) => {
                                setFormData(prev => ({
                                  ...prev,
                                  property_allocations: prev.property_allocations.map((pa, i) =>
                                    i === index
                                      ? { ...pa, allocation_ratio: parseFloat(e.target.value) || 0 }
                                      : pa
                                  )
                                }));
                              }}
                              className="w-20 px-2 py-1 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-white"
                            />
                            <span className="text-sm text-gray-500 dark:text-gray-400">%</span>
                            <button
                              type="button"
                              onClick={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  property_allocations: prev.property_allocations.filter((_, i) => i !== index)
                                }));
                              }}
                              className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                      );
                    })}
                    
                    {/* Total Percentage Display */}
                    <div className="text-sm text-gray-600 dark:text-gray-400 text-right">
                      Gesamt: {formData.property_allocations.reduce((sum, pa) => sum + (pa.allocation_ratio || 0), 0)}%
                    </div>
                  </div>
                )}
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Notizen
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 dark:text-white"
                  placeholder="Zusätzliche Informationen..."
                />
              </div>

              {/* Error Messages */}
              {errors.submit && (
                <div className="bg-red-100 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <p className="text-red-800 dark:text-red-400">{errors.submit}</p>
                </div>
              )}

              {/* Submit Buttons */}
              <div className="flex items-center justify-end gap-3 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Speichern...
                    </>
                  ) : (
                    <>
                      {financing ? 'Aktualisieren' : 'Erstellen'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Right Side - AI Insights */}
          <div className="w-80 bg-gray-50 dark:bg-gray-800 p-6 overflow-y-auto border-l border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Zap className="w-5 h-5 text-blue-600" />
              AI-Insights
            </h3>

            {/* Insight Tabs */}
            <div className="flex mb-4 bg-gray-200 dark:bg-gray-700 rounded-lg p-1">
              <button
                onClick={() => setActiveInsight('benchmark')}
                className={`flex-1 px-3 py-2 text-xs font-medium rounded transition-colors ${
                  activeInsight === 'benchmark'
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                Marktvergleich
              </button>
              <button
                onClick={() => setActiveInsight('calculator')}
                className={`flex-1 px-3 py-2 text-xs font-medium rounded transition-colors ${
                  activeInsight === 'calculator'
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                Rechner
              </button>
              <button
                onClick={() => setActiveInsight('risk')}
                className={`flex-1 px-3 py-2 text-xs font-medium rounded transition-colors ${
                  activeInsight === 'risk'
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                Risiko
              </button>
            </div>

            {/* Insight Content */}
            <div className="space-y-4">
              {activeInsight === 'benchmark' && (
                <>
                  <div className="bg-white dark:bg-gray-900 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-green-600" />
                      Marktvergleich
                    </h4>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Ihr Zinssatz:</span>
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {formatPercentage(formData.interest_rate_apy)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Marktdurchschnitt:</span>
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {formatPercentage(marketData.averageRate)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Bester Zinssatz:</span>
                        <span className="font-semibold text-green-600 dark:text-green-400">
                          {formatPercentage(marketData.bestRate)}
                        </span>
                      </div>
                      {insights.rateComparison.potentialSavings > 0 && (
                        <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Potenzielle Ersparnis:</span>
                            <span className="font-semibold text-green-600 dark:text-green-400">
                              {formatCurrency(insights.rateComparison.potentialSavings)}/Jahr
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="bg-white dark:bg-gray-900 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-blue-600" />
                      Markttrend
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {marketData.trends.forecast}
                    </p>
                  </div>
                </>
              )}

              {activeInsight === 'calculator' && (
                <>
                  <div className="bg-white dark:bg-gray-900 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                      <Calculator className="w-4 h-4 text-blue-600" />
                      Zahlungsrechner
                    </h4>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Monatliche Rate:</span>
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {formatCurrency(insights.monthlyPayment)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Jährliche Belastung:</span>
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {formatCurrency(insights.monthlyPayment * 12)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white dark:bg-gray-900 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                      <ExternalLink className="w-4 h-4 text-purple-600" />
                      Cross-Selling
                    </h4>
                    <div className="space-y-2">
                      <a
                        href="https://www.interhyp.de"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block p-2 bg-blue-50 dark:bg-blue-900/20 rounded text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                      >
                        Interhyp Vergleich
                      </a>
                      <a
                        href="https://www.drklein.de"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block p-2 bg-green-50 dark:bg-green-900/20 rounded text-sm text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
                      >
                        Dr. Klein Beratung
                      </a>
                    </div>
                  </div>
                </>
              )}

              {activeInsight === 'risk' && (
                <>
                  <div className="bg-white dark:bg-gray-900 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                      <AlertTriangle className={`w-4 h-4 ${
                        insights.riskLevel === 'high' ? 'text-red-600' :
                        insights.riskLevel === 'medium' ? 'text-yellow-600' : 'text-green-600'
                      }`} />
                      Risiko-Analyse
                    </h4>
                    <div className={`p-3 rounded-lg ${
                      insights.riskLevel === 'high' ? 'bg-red-50 dark:bg-red-900/20' :
                      insights.riskLevel === 'medium' ? 'bg-yellow-50 dark:bg-yellow-900/20' : 'bg-green-50 dark:bg-green-900/20'
                    }`}>
                      <span className={`text-sm font-medium ${
                        insights.riskLevel === 'high' ? 'text-red-800 dark:text-red-400' :
                        insights.riskLevel === 'medium' ? 'text-yellow-800 dark:text-yellow-400' : 'text-green-800 dark:text-green-400'
                      }`}>
                        {insights.riskLevel === 'high' ? 'Hohes Risiko' :
                         insights.riskLevel === 'medium' ? 'Mittleres Risiko' : 'Niedriges Risiko'}
                      </span>
                    </div>
                  </div>

                  {insights.recommendations.length > 0 && (
                    <div className="bg-white dark:bg-gray-900 rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                        <Award className="w-4 h-4 text-blue-600" />
                        Empfehlungen
                      </h4>
                      <ul className="space-y-2">
                        {insights.recommendations.map((rec, index) => (
                          <li key={index} className="text-sm text-gray-600 dark:text-gray-400 flex items-start gap-2">
                            <span className="text-blue-600 dark:text-blue-400 mt-1">•</span>
                            {rec}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancingModal;

